import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Loader2, LogOut, Plus, Trash2, RotateCcw, Save } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useSeo } from "@/lib/seo";
import { DEFAULT_CONTENT } from "@/lib/defaultContent";
import { Logo } from "@/components/landing/Nav";

const inputClass =
  "w-full rounded-lg border border-white/10 bg-ink px-3 py-2.5 text-sm text-bone placeholder:text-fog/50 focus:border-mint focus:outline-none";
const labelClass = "mb-1.5 block font-mono text-[10px] uppercase tracking-[0.2em] text-fog";

const STATUS_OPTIONS = ["ny", "kontaktad", "klar"];
const ICON_OPTIONS = ["globe", "pentool", "mappin", "megaphone", "search", "palette"];


function CustomersTab({ customers, onDelete }) {
  if (!customers.length) {
    return (
      <p data-testid="customers-empty" className="rounded-2xl border border-white/10 bg-surface p-10 text-center text-sm text-fog">
        Inga kundkonton ännu. Kunder som skapar konto på hemsidan dyker upp här.
      </p>
    );
  }
  return (
    <div className="space-y-3" data-testid="customers-list">
      {customers.map((c) => (
        <article key={c.id} data-testid={`customer-card-${c.id}`} className="flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-white/10 bg-surface p-5">
          <div>
            <p className="font-display text-base font-bold text-bone">{c.name || "–"}</p>
            <p className="mt-0.5 text-sm text-fog">{c.email}</p>
            <div className="mt-2 flex flex-wrap gap-2">
              <span className={`rounded-full px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] ${c.email_verified ? "bg-mint/15 text-mint" : "border border-white/15 text-fog"}`}>
                {c.email_verified ? "Verifierad" : "Ej verifierad"}
              </span>
              <span className="rounded-full border border-white/15 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-fog">
                {c.leads} förfrågningar
              </span>
              <span className="rounded-full border border-white/15 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-fog">
                {c.created_at ? new Date(c.created_at).toLocaleDateString("sv-SE") : ""}
              </span>
            </div>
          </div>
          <button
            data-testid={`customer-delete-${c.id}`}
            onClick={() => window.confirm(`Ta bort kontot för ${c.email}? Kundens förfrågningar behålls.`) && onDelete(c.id)}
            className="flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-xs font-semibold text-fog transition-colors hover:border-red-500/50 hover:text-red-400"
          >
            <Trash2 className="h-3.5 w-3.5" /> Ta bort konto
          </button>
        </article>
      ))}
    </div>
  );
}

function LeadsTab({ leads, onStatus, onDelete }) {
  if (!leads.length) {
    return (
      <p data-testid="leads-empty" className="rounded-2xl border border-white/10 bg-surface p-10 text-center text-sm text-fog">
        Inga förfrågningar ännu. Nya bokningar från hemsidan dyker upp här.
      </p>
    );
  }
  return (
    <div className="space-y-4" data-testid="leads-list">
      <p className="rounded-xl border border-white/10 bg-coal px-4 py-3 text-xs leading-relaxed text-fog">
        När du ändrar status till <span className="text-bone">Kontaktad</span> eller <span className="text-bone">Klar</span> får kunden
        automatiskt ett mejl på sitt språk.
      </p>
      {leads.map((lead) => (
        <article key={lead.id} data-testid={`lead-card-${lead.id}`} className="rounded-2xl border border-white/10 bg-surface p-6">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <p className="font-display text-lg font-bold text-bone">
                {lead.name} {lead.company && <span className="text-fog">· {lead.company}</span>}
              </p>
              <p className="mt-1 text-sm text-fog">
                {lead.email} {lead.phone && `· ${lead.phone}`}
              </p>
              {lead.package && (
                <span className="mt-3 inline-block rounded-full bg-mint/15 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-mint">
                  {lead.package}
                </span>
              )}
              {lead.lang === "en" && (
                <span data-testid={`lead-lang-${lead.id}`} className="ml-2 mt-3 inline-block rounded-full border border-white/15 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-fog">
                  Engelska sidan
                </span>
              )}
            </div>
            <div className="flex items-center gap-3">
              <select
                data-testid={`lead-status-${lead.id}`}
                value={lead.status}
                onChange={(e) => onStatus(lead.id, e.target.value)}
                className={`${inputClass} w-auto appearance-none`}
                aria-label="Status"
              >
                {STATUS_OPTIONS.map((s) => (
                  <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
                ))}
              </select>
              <button
                data-testid={`lead-delete-${lead.id}`}
                onClick={() => onDelete(lead.id)}
                className="rounded-full border border-white/15 p-2.5 text-fog transition-colors hover:border-red-500/50 hover:text-red-400"
                aria-label="Ta bort förfrågan"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </div>
          {lead.message && <p className="mt-4 border-t border-white/10 pt-4 text-sm leading-relaxed text-bone/80">{lead.message}</p>}
          <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.2em] text-fog/60">
            {lead.created_at ? new Date(lead.created_at).toLocaleString("sv-SE") : ""}
          </p>
        </article>
      ))}
    </div>
  );
}

function ItemCard({ testid, onRemove, children }) {
  return (
    <div data-testid={testid} className="relative rounded-2xl border border-white/10 bg-surface p-6">
      <button
        data-testid={`${testid}-remove`}
        onClick={onRemove}
        className="absolute right-4 top-4 rounded-full border border-white/15 p-2 text-fog transition-colors hover:border-red-500/50 hover:text-red-400"
        aria-label="Ta bort"
      >
        <Trash2 className="h-3.5 w-3.5" />
      </button>
      <div className="grid gap-4 sm:grid-cols-2">{children}</div>
    </div>
  );
}

function Field({ label, testid, value, onChange, textarea, full }) {
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <label className={labelClass}>{label}</label>
      {textarea ? (
        <textarea data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} rows={4} className={`${inputClass} resize-y`} />
      ) : (
        <input data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} className={inputClass} />
      )}
    </div>
  );
}

function ServicesEditor({ items, setItems }) {
  const update = (i, key, value) => setItems(items.map((s, j) => (j === i ? { ...s, [key]: value } : s)));
  return (
    <div className="space-y-4">
      {items.map((s, i) => (
        <ItemCard key={i} testid={`service-editor-${i}`} onRemove={() => setItems(items.filter((_, j) => j !== i))}>
          <Field label="Titel" testid={`service-title-${i}`} value={s.title} onChange={(v) => update(i, "title", v)} />
          <div>
            <label className={labelClass}>Ikon</label>
            <select data-testid={`service-icon-${i}`} value={s.icon} onChange={(e) => update(i, "icon", e.target.value)} className={`${inputClass} appearance-none`}>
              {ICON_OPTIONS.map((o) => (
                <option key={o} value={o}>{o}</option>
              ))}
            </select>
          </div>
          <Field label="Beskrivning" testid={`service-description-${i}`} value={s.description} onChange={(v) => update(i, "description", v)} textarea full />
        </ItemCard>
      ))}
      <button
        data-testid="add-service-button"
        onClick={() => setItems([...items, { icon: "globe", title: "Ny tjänst", description: "" }])}
        className="flex items-center gap-2 rounded-full border border-dashed border-white/25 px-5 py-3 text-sm font-semibold text-fog transition-colors hover:border-mint hover:text-mint"
      >
        <Plus className="h-4 w-4" /> Lägg till tjänst
      </button>
    </div>
  );
}

function PackagesEditor({ items, setItems }) {
  const update = (i, key, value) => setItems(items.map((p, j) => (j === i ? { ...p, [key]: value } : p)));
  return (
    <div className="space-y-4">
      {items.map((p, i) => (
        <ItemCard key={i} testid={`package-editor-${i}`} onRemove={() => setItems(items.filter((_, j) => j !== i))}>
          <Field label="Namn" testid={`package-name-${i}`} value={p.name} onChange={(v) => update(i, "name", v)} />
          <Field label="Pris" testid={`package-price-input-${i}`} value={p.price} onChange={(v) => update(i, "price", v)} />
          <Field label="Undertext" testid={`package-subtitle-${i}`} value={p.subtitle} onChange={(v) => update(i, "subtitle", v)} full />
          <Field label="Badge (valfritt)" testid={`package-badge-input-${i}`} value={p.badge || ""} onChange={(v) => update(i, "badge", v)} />
          <Field label="Betallänk (valfritt – t.ex. Stripe eller Swish)" testid={`package-paylink-input-${i}`} value={p.payLink || ""} onChange={(v) => update(i, "payLink", v)} />
          <div className="flex items-end pb-2">
            <label className="flex cursor-pointer items-center gap-3 text-sm text-bone">
              <input
                data-testid={`package-recommended-${i}`}
                type="checkbox"
                checked={!!p.recommended}
                onChange={(e) => update(i, "recommended", e.target.checked)}
                className="h-4 w-4 accent-[#2AE89B]"
              />
              Markera som rekommenderad
            </label>
          </div>
          <Field
            label="Funktioner (en per rad)"
            testid={`package-features-${i}`}
            value={(p.features || []).join("\n")}
            onChange={(v) => update(i, "features", v.split("\n").filter((f) => f.trim()))}
            textarea
            full
          />
        </ItemCard>
      ))}
      <button
        data-testid="add-package-button"
        onClick={() => setItems([...items, { name: "NYTT PAKET", price: "0 kr", subtitle: "", badge: "", recommended: false, features: [] }])}
        className="flex items-center gap-2 rounded-full border border-dashed border-white/25 px-5 py-3 text-sm font-semibold text-fog transition-colors hover:border-mint hover:text-mint"
      >
        <Plus className="h-4 w-4" /> Lägg till paket
      </button>
    </div>
  );
}

function MaintenanceEditor({ items, setItems }) {
  const update = (i, key, value) => setItems(items.map((m, j) => (j === i ? { ...m, [key]: value } : m)));
  return (
    <div className="space-y-4">
      {items.map((m, i) => (
        <ItemCard key={i} testid={`maintenance-editor-${i}`} onRemove={() => setItems(items.filter((_, j) => j !== i))}>
          <Field label="Namn" testid={`maintenance-name-${i}`} value={m.name} onChange={(v) => update(i, "name", v)} />
          <Field label="Pris" testid={`maintenance-price-input-${i}`} value={m.price} onChange={(v) => update(i, "price", v)} />
          <Field label="Beskrivning" testid={`maintenance-description-${i}`} value={m.description} onChange={(v) => update(i, "description", v)} full />
          <Field label="Betallänk (valfritt – t.ex. Stripe eller Swish)" testid={`maintenance-paylink-input-${i}`} value={m.payLink || ""} onChange={(v) => update(i, "payLink", v)} full />
          <Field
            label="Funktioner (en per rad)"
            testid={`maintenance-features-${i}`}
            value={(m.features || []).join("\n")}
            onChange={(v) => update(i, "features", v.split("\n").filter((f) => f.trim()))}
            textarea
            full
          />
        </ItemCard>
      ))}
      <button
        data-testid="add-maintenance-button"
        onClick={() => setItems([...items, { name: "Nytt abonnemang", price: "0 kr/mån", description: "", badge: "", recommended: false, features: [] }])}
        className="flex items-center gap-2 rounded-full border border-dashed border-white/25 px-5 py-3 text-sm font-semibold text-fog transition-colors hover:border-mint hover:text-mint"
      >
        <Plus className="h-4 w-4" /> Lägg till abonnemang
      </button>
    </div>
  );
}

function ProjectsEditor({ items, setItems }) {
  const [uploading, setUploading] = useState({});
  const update = (i, key, value) => setItems(items.map((p, j) => (j === i ? { ...p, [key]: value } : p)));
  const handleUpload = async (i, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading((u) => ({ ...u, [i]: true }));
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/upload", form);
      update(i, "image", data.url);
      toast.success("Bilden är uppladdad – glöm inte att spara ändringarna.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading((u) => ({ ...u, [i]: false }));
      e.target.value = "";
    }
  };
  return (
    <div className="space-y-4">
      <p className="rounded-xl border border-white/10 bg-coal px-4 py-3 text-xs leading-relaxed text-fog">
        Det första projektet i listan visas extra stort på hemsidan. Klistra in en bildlänk (URL) eller
        ladda upp en egen bild. Glöm inte att spara ändringarna längst ner.
      </p>
      {items.map((p, i) => (
        <ItemCard key={i} testid={`project-editor-${i}`} onRemove={() => setItems(items.filter((_, j) => j !== i))}>
          <Field label="Titel" testid={`project-title-${i}`} value={p.title} onChange={(v) => update(i, "title", v)} />
          <Field label="Kategori" testid={`project-category-${i}`} value={p.category} onChange={(v) => update(i, "category", v)} />
          <Field label="Bildlänk (URL)" testid={`project-image-${i}`} value={p.image} onChange={(v) => update(i, "image", v)} full />
          <div className="sm:col-span-2 flex flex-wrap items-center gap-4">
            <input
              id={`project-upload-input-${i}`}
              data-testid={`project-upload-input-${i}`}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              className="hidden"
              onChange={(e) => handleUpload(i, e)}
            />
            <label
              htmlFor={`project-upload-input-${i}`}
              data-testid={`project-upload-button-${i}`}
              className="flex cursor-pointer items-center gap-2 rounded-full border border-white/20 px-5 py-2.5 text-sm font-semibold text-bone transition-colors hover:border-mint hover:text-mint"
            >
              {uploading[i] ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
              {uploading[i] ? "Laddar upp…" : "Ladda upp bild"}
            </label>
            {p.image && (
              <img
                src={p.image}
                alt={`Förhandsvisning ${p.title}`}
                className="h-14 w-24 rounded-lg border border-white/10 object-cover"
              />
            )}
          </div>
          <Field label="Beskrivning" testid={`project-description-${i}`} value={p.description} onChange={(v) => update(i, "description", v)} textarea full />
        </ItemCard>
      ))}
      <button
        data-testid="add-project-button"
        onClick={() => setItems([...items, { title: "Nytt projekt", category: "", description: "", image: "" }])}
        className="flex items-center gap-2 rounded-full border border-dashed border-white/25 px-5 py-3 text-sm font-semibold text-fog transition-colors hover:border-mint hover:text-mint"
      >
        <Plus className="h-4 w-4" /> Lägg till projekt
      </button>
    </div>
  );
}

function SocialsEditor({ socials, setSocials }) {
  const fields = [
    { key: "instagram", label: "Instagram" },
    { key: "facebook", label: "Facebook" },
    { key: "linkedin", label: "LinkedIn" },
  ];
  return (
    <div className="rounded-2xl border border-white/10 bg-surface p-6 sm:p-8">
      <p className="mb-6 text-xs leading-relaxed text-fog">
        Klistra in hela adressen, t.ex. https://instagram.com/nivadigital. Lämna fältet tomt för att dölja
        ikonen i footern. Glöm inte att spara ändringarna längst ner.
      </p>
      <div className="grid gap-4">
        {fields.map((f) => (
          <Field
            key={f.key}
            label={f.label}
            testid={`social-input-${f.key}`}
            value={socials[f.key] || ""}
            onChange={(v) => setSocials({ ...socials, [f.key]: v })}
          />
        ))}
      </div>
    </div>
  );
}

function HeroEditor({ hero, setHero }) {
  const update = (key) => (v) => setHero({ ...hero, [key]: v });
  return (
    <div className="space-y-4">
      <p className="rounded-xl border border-white/10 bg-coal px-4 py-3 text-xs leading-relaxed text-fog">
        Här ändrar du den stora rubriken och ingressen högst upp på hemsidan. Accenttexten är den gröna
        delen av rubriken. Glöm inte att spara ändringarna längst ner.
      </p>
      <div className="rounded-2xl border border-white/10 bg-surface p-6 sm:p-8">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Badge-text (liten text ovanför rubriken)" testid="hero-badge" value={hero.badge || ""} onChange={update("badge")} full />
          <Field label="Rubrik – rad 1" testid="hero-heading1" value={hero.heading1 || ""} onChange={update("heading1")} />
          <Field label="Rubrik – rad 2 (före accenten)" testid="hero-heading2" value={hero.heading2 || ""} onChange={update("heading2")} />
          <Field label="Accenttext (grön del av rubriken)" testid="hero-accent" value={hero.accent || ""} onChange={update("accent")} />
          <Field label="Ingress (text under rubriken)" testid="hero-subtext" value={hero.subtext || ""} onChange={update("subtext")} textarea full />
        </div>
      </div>
    </div>
  );
}

function FaqEditor({ faq, setFaq }) {
  const items = faq.items || [];
  const update = (key) => (v) => setFaq({ ...faq, [key]: v });
  const updateItem = (i, key, value) =>
    setFaq({ ...faq, items: items.map((it, j) => (j === i ? { ...it, [key]: value } : it)) });
  return (
    <div className="space-y-4">
      <p className="rounded-xl border border-white/10 bg-coal px-4 py-3 text-xs leading-relaxed text-fog">
        Här ändrar du texten i sektion 08, Vanliga frågor. Glöm inte att spara ändringarna längst ner.
      </p>
      <div className="rounded-2xl border border-white/10 bg-surface p-6 sm:p-8">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Tagg (liten text ovanför rubriken)" testid="faq-tag" value={faq.tag || ""} onChange={update("tag")} />
          <Field label="Rubrik" testid="faq-heading" value={faq.heading || ""} onChange={update("heading")} />
          <Field label="Undertext" testid="faq-sub" value={faq.sub || ""} onChange={update("sub")} textarea full />
        </div>
      </div>
      {items.map((item, i) => (
        <ItemCard key={i} testid={`faq-editor-${i}`} onRemove={() => setFaq({ ...faq, items: items.filter((_, j) => j !== i) })}>
          <Field label="Fråga" testid={`faq-question-${i}`} value={item.q} onChange={(v) => updateItem(i, "q", v)} full />
          <Field label="Svar" testid={`faq-answer-${i}`} value={item.a} onChange={(v) => updateItem(i, "a", v)} textarea full />
        </ItemCard>
      ))}
      <button
        data-testid="add-faq-button"
        onClick={() => setFaq({ ...faq, items: [...items, { q: "Ny fråga", a: "" }] })}
        className="flex items-center gap-2 rounded-full border border-dashed border-white/25 px-5 py-3 text-sm font-semibold text-fog transition-colors hover:border-mint hover:text-mint"
      >
        <Plus className="h-4 w-4" /> Lägg till fråga
      </button>
    </div>
  );
}

function BookingEditor({ booking, setBooking }) {
  const update = (key) => (v) => setBooking({ ...booking, [key]: v });
  return (
    <div className="space-y-4">
      <p className="rounded-xl border border-white/10 bg-coal px-4 py-3 text-xs leading-relaxed text-fog">
        Här ändrar du texten i sektion 09, kontakt- och bokningsdelen. Glöm inte att spara ändringarna längst ner.
      </p>
      <div className="rounded-2xl border border-white/10 bg-surface p-6 sm:p-8">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Tagg (liten text ovanför rubriken)" testid="booking-tag" value={booking.tag || ""} onChange={update("tag")} />
          <Field label="Rubrik – rad 1" testid="booking-heading1" value={booking.heading1 || ""} onChange={update("heading1")} />
          <Field label="Rubrik – accent (grön del)" testid="booking-headingaccent" value={booking.headingAccent || ""} onChange={update("headingAccent")} />
          <Field label="Text" testid="booking-text" value={booking.text || ""} onChange={update("text")} textarea full />
          <Field label="Chip 1" testid="booking-chip1" value={booking.chip1 || ""} onChange={update("chip1")} />
          <Field label="Chip 2" testid="booking-chip2" value={booking.chip2 || ""} onChange={update("chip2")} />
        </div>
      </div>
    </div>
  );
}

const TABS = [
  { id: "leads", label: "Förfrågningar", testid: "admin-tab-leads" },
  { id: "kunder", label: "Kunder", testid: "admin-tab-kunder" },
  { id: "hero", label: "Hero & texter", testid: "admin-tab-hero" },
  { id: "tjanster", label: "Tjänster", testid: "admin-tab-tjanster" },
  { id: "paket", label: "Paket & priser", testid: "admin-tab-paket" },
  { id: "forvaltning", label: "Förvaltning", testid: "admin-tab-forvaltning" },
  { id: "projekt", label: "Projekt", testid: "admin-tab-projekt" },
  { id: "faq", label: "Vanliga frågor", testid: "admin-tab-faq" },
  { id: "boka", label: "Boka & kontakt", testid: "admin-tab-boka" },
  { id: "sociala", label: "Sociala länkar", testid: "admin-tab-sociala" },
];

function Dashboard({ user }) {
  const navigate = useNavigate();
  const auth = useAuth();
  const [tab, setTab] = useState("leads");
  const [leads, setLeads] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [services, setServices] = useState(DEFAULT_CONTENT.services);
  const [packages, setPackages] = useState(DEFAULT_CONTENT.packages);
  const [maintenance, setMaintenance] = useState(DEFAULT_CONTENT.maintenance);
  const [projects, setProjects] = useState(DEFAULT_CONTENT.projects);
  const [socials, setSocials] = useState(DEFAULT_CONTENT.socials);
  const [hero, setHero] = useState(DEFAULT_CONTENT.hero);
  const [faq, setFaq] = useState(DEFAULT_CONTENT.faq);
  const [booking, setBooking] = useState(DEFAULT_CONTENT.booking);
  const [saving, setSaving] = useState(false);
  const [contentLoaded, setContentLoaded] = useState(false);

  useEffect(() => {
    api.get("/leads").then((r) => setLeads(r.data)).catch(() => {});
    api.get("/admin/customers").then((r) => setCustomers(r.data)).catch(() => {});
    api.get("/content").then((r) => {
      if (r.data?.services) {
        setServices(r.data.services);
        setPackages(r.data.packages);
        setMaintenance(r.data.maintenance);
        setProjects(r.data.projects || DEFAULT_CONTENT.projects);
        setSocials(r.data.socials || DEFAULT_CONTENT.socials);
        setHero(r.data.hero || DEFAULT_CONTENT.hero);
        setFaq(r.data.faq || DEFAULT_CONTENT.faq);
        setBooking(r.data.booking || DEFAULT_CONTENT.booking);
        setContentLoaded(true);
      }
    }).catch(() => toast.error("Kunde inte hämta innehållet från servern. Ladda om sidan innan du sparar."));
  }, []);

  const saveContent = async () => {
    setSaving(true);
    try {
      await api.put("/content", { services, packages, maintenance, projects, socials, hero, faq, booking });
      toast.success("Sparat! Ändringarna syns direkt på hemsidan.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const resetContent = async () => {
    try {
      const { data } = await api.post("/content/reset");
      setServices(data.services);
      setPackages(data.packages);
      setMaintenance(data.maintenance);
      setProjects(data.projects);
      setSocials(data.socials);
      setHero(data.hero);
      setFaq(data.faq);
      setBooking(data.booking);
      setContentLoaded(true);
      toast.success("Innehållet har återställts till standard.");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const setLeadStatus = async (id, status) => {
    setLeads(leads.map((l) => (l.id === id ? { ...l, status } : l)));
    try {
      await api.patch(`/leads/${id}`, { status });
    } catch {
      toast.error("Kunde inte uppdatera status.");
    }
  };

  const deleteLead = async (id) => {
    setLeads(leads.filter((l) => l.id !== id));
    try {
      await api.delete(`/leads/${id}`);
      toast.success("Förfrågan borttagen.");
    } catch {
      toast.error("Kunde inte ta bort förfrågan.");
    }
  };

  const deleteCustomer = async (id) => {
    try {
      await api.delete(`/admin/customers/${id}`);
      setCustomers(customers.filter((c) => c.id !== id));
      toast.success("Kundkontot är borttaget.");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const logout = async () => {
    await auth.logout();
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-ink font-body text-bone">
      <header className="sticky top-0 z-40 border-b border-white/10 bg-ink/90 backdrop-blur-xl">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-4 py-4 sm:px-6">
          <div className="flex items-center gap-4">
            <Logo onClick={() => (window.location.href = "/")} />
            <span className="rounded-full bg-mint/15 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-mint">
              Admin
            </span>
          </div>
          <div className="flex items-center gap-4">
            <span data-testid="admin-user-email" className="hidden text-xs text-fog sm:block">{user.email}</span>
            <button
              data-testid="admin-logout-button"
              onClick={logout}
              className="flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-xs font-semibold text-fog transition-colors hover:border-mint hover:text-mint"
            >
              <LogOut className="h-3.5 w-3.5" /> Logga ut
            </button>
          </div>
        </div>
      </header>

      <main data-testid="admin-dashboard" className="mx-auto max-w-5xl px-4 py-10 sm:px-6">
        <div className="mb-8 grid grid-cols-3 gap-4">
          {[
            { label: "Förfrågningar", value: leads.length, testid: "stat-leads" },
            { label: "Kunder", value: customers.length, testid: "stat-customers" },
            { label: "Paket", value: packages.length, testid: "stat-packages" },
          ].map((s) => (
            <div key={s.label} data-testid={s.testid} className="rounded-2xl border border-white/10 bg-surface p-5 text-center">
              <p className="font-display text-3xl font-extrabold text-mint">{s.value}</p>
              <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-fog">{s.label}</p>
            </div>
          ))}
        </div>

        <div className="mb-8 flex flex-wrap gap-2">
          {TABS.map((t) => (
            <button
              key={t.id}
              data-testid={t.testid}
              onClick={() => setTab(t.id)}
              className={`rounded-full px-5 py-2.5 text-sm font-semibold transition-colors duration-300 ${
                tab === t.id ? "bg-mint text-ink" : "border border-white/15 text-fog hover:text-bone"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === "leads" && <LeadsTab leads={leads} onStatus={setLeadStatus} onDelete={deleteLead} />}
        {tab === "kunder" && <CustomersTab customers={customers} onDelete={deleteCustomer} />}
        {tab === "hero" && <HeroEditor hero={hero} setHero={setHero} />}
        {tab === "tjanster" && <ServicesEditor items={services} setItems={setServices} />}
        {tab === "paket" && <PackagesEditor items={packages} setItems={setPackages} />}
        {tab === "forvaltning" && <MaintenanceEditor items={maintenance} setItems={setMaintenance} />}
        {tab === "projekt" && <ProjectsEditor items={projects} setItems={setProjects} />}
        {tab === "faq" && <FaqEditor faq={faq} setFaq={setFaq} />}
        {tab === "boka" && <BookingEditor booking={booking} setBooking={setBooking} />}
        {tab === "sociala" && <SocialsEditor socials={socials} setSocials={setSocials} />}

        {tab !== "leads" && tab !== "kunder" && (
          <div className="sticky bottom-6 mt-10 flex flex-wrap gap-3 rounded-2xl border border-white/10 bg-coal/95 p-4 backdrop-blur-xl">
            <button
              data-testid="save-content-button"
              onClick={saveContent}
              disabled={saving || !contentLoaded}
              className="flex items-center gap-2 rounded-full bg-mint px-6 py-3 text-sm font-bold text-ink transition-colors hover:bg-bone disabled:opacity-60"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Spara ändringar
            </button>
            <button
              data-testid="reset-content-button"
              onClick={resetContent}
              className="flex items-center gap-2 rounded-full border border-white/15 px-6 py-3 text-sm font-semibold text-fog transition-colors hover:border-mint hover:text-mint"
            >
              <RotateCcw className="h-4 w-4" /> Återställ standard
            </button>
          </div>
        )}
      </main>
    </div>
  );
}

export default function Admin() {
  const { user } = useAuth();
  useSeo({ title: "Adminpanel – Nivå Digital", description: "Intern adminpanel.", noindex: true });

  if (user === undefined) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-ink">
        <Loader2 className="h-8 w-8 animate-spin text-mint" />
      </div>
    );
  }
  if (!user) return <Navigate to="/logga-in?next=/admin" replace />;
  if (user.role !== "admin") return <Navigate to="/mitt-konto" replace />;
  return <Dashboard user={user} />;
}
